import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deliverOrderLine } from '../../api/assets';
import { listSites } from '../../api/sites';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import type { DeliverableLine } from '../../types/api';

export function DeliverModal({ line, open, onClose }: { line: DeliverableLine; open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [serialNumber, setSerialNumber] = useState('');
  const [condition, setCondition] = useState('');
  const [warrantyEndDate, setWarrantyEndDate] = useState('');
  const [installDate, setInstallDate] = useState('');
  const [currentSiteId, setCurrentSiteId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const sitesQuery = useQuery({
    queryKey: ['sites', line.order.customerOrganisationId],
    queryFn: () => listSites(line.order.customerOrganisationId),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setSerialNumber('');
    setCondition('');
    setWarrantyEndDate('');
    setInstallDate('');
    setCurrentSiteId(line.order.siteId);
    setError(null);
  }, [open, line]);

  const mutation = useMutation({
    mutationFn: () =>
      deliverOrderLine({
        orderLineId: line.id,
        serialNumber: serialNumber || undefined,
        condition: condition || undefined,
        warrantyEndDate: warrantyEndDate || undefined,
        installDate: installDate || undefined,
        currentSiteId: currentSiteId !== line.order.siteId ? currentSiteId : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliverable'] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      showToast(`${line.customerCatalogItem.name} marked as delivered.`);
      onClose();
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not record the delivery.')),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Deliver: ${line.customerCatalogItem.name}`}
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button form="deliver-form" type="submit" isLoading={mutation.isPending}>
            Mark delivered
          </Button>
        </>
      }
    >
      <form id="deliver-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}

        <Input label="Serial number" autoFocus value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} />
        <Input label="Condition" placeholder="e.g. New" value={condition} onChange={(e) => setCondition(e.target.value)} />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Warranty end date"
            type="date"
            value={warrantyEndDate}
            onChange={(e) => setWarrantyEndDate(e.target.value)}
          />
          <Input label="Install date" type="date" value={installDate} onChange={(e) => setInstallDate(e.target.value)} />
        </div>

        <Select label="Delivered to site" value={currentSiteId} onChange={(e) => setCurrentSiteId(e.target.value)}>
          {sitesQuery.data?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </form>
    </Modal>
  );
}
